import { isPlatformServer } from '@angular/common';
import { HttpBackend, HttpClient } from '@angular/common/http';
import {
  EventEmitter,
  Inject,
  Injectable,
  InjectionToken,
  Injector,
  makeStateKey,
  Optional,
  PLATFORM_ID,
  TransferState
} from '@angular/core';
import { ConfigDefaults } from './config.defaults';
import { ConfigResponse, INgcConfig } from './config.interface';

declare let delta: any;

export const NGC_CONFIG = new InjectionToken('NGC_CONFIG');
const CONFIG_KEY = makeStateKey('CONFIG');
export const HEADER_CONFIGURATION_URL: InjectionToken<string> =
  new InjectionToken<string>('');

@Injectable({
  providedIn: 'root'
})
export class ApplicationConfig {
  private _config;
  private headerConfigurationURl: string;

  constructor(
    @Inject(PLATFORM_ID) private _platformId: Object,
    @Optional() @Inject(HEADER_CONFIGURATION_URL) private configUrl: string,
    private injector: Injector,
    private cache: TransferState,
    private httpBackend: HttpBackend
  ) {
    this.headerConfigurationURl = configUrl;
    // BOOTSTRAP THE CONFIG FROM THE PROPERTIES FILE depending on the Environment
    if (!this.headerConfigurationURl) {
      if (isPlatformServer(this._platformId)) {
        this._config = this.injector.get('CONFIG_TOKEN');
        this.cache.set(CONFIG_KEY, this._config);
      } else {
        // assume platform browser
        if (window) {
          // try the window object

          let transferredState = this.cache.get(CONFIG_KEY, undefined as any);
          if (transferredState) {
            // console.log('Transfer State IDP' + transferredState);
            this._config = transferredState;
          } else if (
            window['HEADER'] !== undefined &&
            window['HEADER'] !== null
          ) {
            transferredState = window['HEADER'];
            this._config = transferredState;
          } else {
            this._config = {};
          }
          // console.log('Transfer State Config' + this._config);
        } else {
          // try to load from external URL
          // TODO
          this._config = {};
        }
      }
    }
    // console.log('Application Config Property' + this._config);
  }

  loadConfiguration(): Promise<boolean> {
    const http = new HttpClient(this.httpBackend);
    return http
      .get<ConfigResponse>(this.headerConfigurationURl)
      .toPromise()
      .then((response) => {
        this._config = response;
      })
      .then((_) => Promise.resolve(true))
      .catch((error) => {
        console.error('Error loading info', error);
        return Promise.resolve(false);
      });
  }

  get platformId() {
    return this._platformId;
  }

  get onServer() {
    return isPlatformServer(this._platformId);
  }

  get onBrowser() {
    return !this.onServer;
  }

  get(key: string) {
    let value = '';
    if (this._config) {
      value = this._config[`${key}`];
    }
    return value ? value : '';
  }

  loadConfig() {
    return this._config;
  }
}

@Injectable({
  providedIn: 'root'
})
export class NgcConfig {
  _emitter = new EventEmitter();

  private values: INgcConfig = new ConfigDefaults();

  constructor(@Inject(NGC_CONFIG) config: INgcConfig = null) {
    this.values = Object.assign({}, this.values, config || {});
  }

  update(config: INgcConfig) {
    this.values = Object.assign({}, this.values, config || {});
    this._emitter.emit();
  }

  get(key: string) {
    return (<any>this.values)[key];
  }
}

// Internal Decorator
export function NgcConfigurable(config = { changeDetectorProperty: 'cd' }) {
  return function (constructor: Function) {
    const { ngOnInit, ngOnDestroy } = constructor.prototype;

    constructor.prototype.ngOnInit = function () {
      const changeDetectorRef = this[config.changeDetectorProperty];

      if (!changeDetectorRef || !changeDetectorRef.markForCheck) {
        throw Error(
          `NgcConfig: invalid ChangeDetectorRef at property "${config.changeDetectorProperty}"`
        );
      }

      this.ngcConfigSubscription = this.config._emitter.subscribe(() => {
        if (this.ngcOnConfigChanges) {
          this.ngcOnConfigChanges();
        }
        changeDetectorRef.markForCheck();
      });

      if (ngOnInit) {
        ngOnInit.call(this);
      }
    };

    constructor.prototype.ngOnDestroy = function () {
      if (this.ngcConfigSubscription) {
        this.ngcConfigSubscription.unsubscribe();
        this.ngcConfigSubscription = null;
      }

      if (ngOnDestroy) {
        ngOnDestroy.call(this);
      }
    };
  };
}
